/// <reference path="../../typings/browser.d.ts" />
/// <reference path="../../customtypings/window.d.ts" />

/**
 * Connection Room service
 */

namespace Application.Services {
    'use strict';

    import IBlock = Application.Models.IBlock;
    import DeviceParams = Application.Models.DeviceParams;

    /**
     * ConnectionRoomService
     */
    export class StompService {
        public static $name: string = "stompService";
        public static $inject: string[] = ['$q', '$interval', '$timeout', '$stomp', '$rootScope', 'backendService'];
        private static messageHeaderName: string = "message";

        private qService: ng.IQService;
        private $interval: ng.IIntervalService;
        private $timeout: ng.ITimeoutService;
        private ngStomp: ngStomp;
        private $rootScope: ng.IRootScopeService;
        private backendService: BackendService;

        private backendBaseUrl: string = window.backendUrl;
        // private servicePath: string = 'flexocore/';
        private servicePath: string = '';
        private stompBackendUrl: string;
        private connected: boolean;
        private listeners: Listeners; 
        private pingInterval: ng.IPromise<{}>;
        private debug: boolean = false;
        private reconnected: boolean;
        private subscriptions: any[] = [];

        constructor($q: ng.IQService,
                    $interval: ng.IIntervalService,
                    $timeout: ng.ITimeoutService,
                    ngStomp: ngStomp,
                    $rootScope: ng.IRootScopeService,
                    backendService: BackendService) {
            this.qService = $q;
            this.$interval = $interval;
            this.$timeout = $timeout;
            this.ngStomp = ngStomp;
            this.$rootScope = $rootScope;
            this.backendService = backendService;
            if (this.backendBaseUrl && 
                this.backendBaseUrl.indexOf("http://") !== 0  && this.backendBaseUrl.indexOf("https://") !== 0 ) {
                this.backendBaseUrl = window.location.origin +
                                      window.location.pathname +
                                      this.backendBaseUrl;
            }

            this.stompBackendUrl = this.backendBaseUrl + this.servicePath + 'stomp';
            this.listeners = new Listeners();
            this.$rootScope.$watch('vars.isUserLoggedIn', (newVal: boolean, oldVal: boolean) => {
                if (newVal !== oldVal) {
                    if (!newVal && this.connected) {
                        this.disconnect();
                    } else if (newVal && !this.connected) {
                        this.connect();
                    }
                }
            });
        }
        
        public register(component: string, operation: string, callback: (flexEvent: Models.IFlexEvent) => void): IEventSubscription {
            return this.listeners.register(component, operation, callback);
        }

        public unregister(subscription: IEventSubscription): void {
            this.listeners.unregister(subscription);
        }

        public bulkUnregister(subscriptions: IEventSubscription[]): void {
            for (let subscription of subscriptions) {
                this.safeUnregister(subscription);
            }
        }

        public safeUnregister(subscription: IEventSubscription): void {
            if (subscription) {
                this.listeners.unregister(subscription);
            }
        }

        public disconnect(callback?: (() => void)): ng.IPromise<void> {
            if (this.pingInterval)
                this.$interval.cancel(this.pingInterval);
            if (this.ngStomp && this.ngStomp.unsubscribe) {
                this.subscriptions.forEach((subscription: any) => {
                    this.ngStomp.unsubscribe(subscription);
                });
                this.subscriptions = [];

            }
            return this.ngStomp.disconnect().then(() => {
                this.connected = false;
                console.log("Disconnected!");
                return;
            });
        }
        
        public connect(): void {
            if (this.connected) {
                console.log("Reconnecting to Stomp- " + new Date());
                this.disconnect().then(() => {
                    this.connect();
                });
            } else {
                console.log("Connecting to Stomp- " + new Date());
                this.ngStomp.connect(this.stompBackendUrl, {}).then((frame: IStrompFrame) => {
                    if (this.reconnected === true){
                        this.$timeout(() => {
                            this.$rootScope.$emit('stompReconnect',{message:'Stomp reconnected'});
                            this.reconnected = false;
                        },0);
                    }
                    this.connected = true;
                    console.log("connected " + frame + " " + new Date());
                    this.subscriptions.push(this.ngStomp.subscribe('/topic/flexEvent', (message:  Models.IFlexEvent) => {
                        this.broadcastEvent(message);
                    }));
                    // example of connection to a group topic:
                    // this.ngStomp.subscribe('/topic/group/EBU', (message:  Models.IFlexEvent) => {
                    //     console.log("Message to group EBU!! " + message);
                    // });
                    this.ngStomp.onError((err: IStrompFrame | string ) => {
                        if ((err as IStrompFrame).headers) {
                            let message: string = (err as IStrompFrame).headers[StompService.messageHeaderName];
                            if (message && message.indexOf("org.springframework.security.access.AccessDeniedException") > -1) {
                                console.log("Access denied to one of the topics: " + err);
                                return;
                            }
                        }
                        console.log("Error on STOMP! [" + err + "] Trying reconnection " + new Date());
                        this.waitReconnection();
                    });

                }).catch((reason: any) => { // tslint:disable-line
                    console.log("Error on connect! " + reason);
                    this.$timeout(() => {
                        this.waitReconnection();
                    }, 1000);
                });
            }
        }

        public dataSubscribe(type: string, id: string, received: (blockData: Models.DeviceParams) => void): IStompSubscription {
            if (this.connected) {
                const topic = `/topic/${type}/${id}`;
                const iStompSubscription = this.ngStomp.subscribe(topic, (message:  Models.IDeviceParams) => {
                    received(new DeviceParams(message));
                }) as IStompSubscription;
                this.subscriptions.push(iStompSubscription);
                return iStompSubscription;
            } else {
                return undefined;
            }
        }

        public thumbnailSubscribe(blockId: number, received: (thumbnail: { data: string}) => void): IStompSubscription {
            if (this.connected) {
                const iStompSubscription = this.ngStomp.subscribe(`/topic/thumbnails/${blockId}`, (message: { data: string}) => {
                    received(message);
                }) as IStompSubscription;
                this.subscriptions.push(iStompSubscription);
                return iStompSubscription;
            } else {
                return undefined;
            }
        }

        public send(channel: string, message: Object): ng.IPromise<{}> {
            return this.ngStomp.send("/app/" + channel, message, {});
        }

        private waitReconnection(): void {
            // with this url we make sure the user is logged in when reconnected
            this.reconnected = false;
            let reconnectUrl: string = this.stompBackendUrl + "/info";
            this.backendService
                .checkHealth(reconnectUrl, true,  this.qService.defer(), CallType.get, 300, undefined, true, 1)
                .then(() => {
                    this.connect();
                    this.reconnected = true;
                });
        }
        
        private broadcastEvent(flexEvent: Models.IFlexEvent): void {
            if (this.debug)
                console.log("Event received: " , flexEvent);
            this.listeners.getCallbacks(flexEvent).forEach( (callback: (flexEvent: Models.IFlexEvent) => void) => {
                this.$timeout(() => { // force scope apply
                    callback(flexEvent);
                });
            });
        }
    }
    
    interface IStompMessage {
        body: string;
    }
    
    class IListenersMap  {
        [key: string]: IComponentListenersMap;
    }
    
    class Listeners {
        private listenersMap: IListenersMap = {};
        
        public register(component: string, 
                        operation: string, callback: (flexEvent: Models.IFlexEvent) => void): IEventSubscription {
            let componetMap: IComponentListenersMap = this.getOrRegisterComponent(component);
            this.registerCallback(componetMap, operation, callback);
            return {component: component, operation: operation, callback: callback};
        }

        public unregister(subscription: IEventSubscription): void {
            let componentMap: IComponentListenersMap = this.getOrRegisterComponent(subscription.component);
            let callBackList: ((flexEvent: Models.IFlexEvent) => void)[] 
                = this.getCallbackListOrCreate(componentMap, subscription.operation);
            let index: number = callBackList.indexOf(subscription.callback);
            if (index >= 0) {
                callBackList.splice(index, 1);
            } else {
                console.warn("CALLBACK NOT FOUND FOR " + subscription.component + "/" + subscription.operation);
            }
        }
        
        public getCallbacks(flexEvent: Models.IFlexEvent): ((flexEvent: Models.IFlexEvent) => void)[] {
            let componentListersMap: IComponentListenersMap = this.getOrRegisterComponent(flexEvent.component);
            // console.log(`Obtaining callbacks for ${flexEvent.component}:${flexEvent.operation}`)
            const properCallbacks = this.getCallbackListOrCreate(componentListersMap, flexEvent.operation);
            const allOperationsCallbacks = this.getCallbackListOrCreate(componentListersMap, Models.ALL_OPERATIONS);
            // console.log(`Obtaining callbacks for ${flexEvent.component}:${flexEvent.operation}. All operations callbacks: ${allOperationsCallbacks.length}`);
            return properCallbacks
                .concat(
                    allOperationsCallbacks
                );
        } 
        
        private getOrRegisterComponent(component: string): IComponentListenersMap {
            if (this.listenersMap[component] === undefined)
                this.listenersMap[component] = {};
            return this.listenersMap[component];
        }
        
        private registerCallback(componentListenersMap: IComponentListenersMap, 
                                 operation: string, callback: (flexEvent: Models.IFlexEvent) => void): void {
            if (componentListenersMap[operation] === undefined)
                componentListenersMap[operation] = [];
            this.getCallbackListOrCreate(componentListenersMap, operation).push(callback);
        }
        
        private getCallbackListOrCreate(componentListenersMap: IComponentListenersMap,
                                        operation: string): ((flexEvent: Models.IFlexEvent) => void)[] {
            if (componentListenersMap[operation] === undefined)
                componentListenersMap[operation] = [];
            return componentListenersMap[operation];
        }
    }
    
    interface IComponentListenersMap {
        [key: string]: ((flexEvent: Models.IFlexEvent) => void)[];
    }

    export interface IEventSubscription {
        component: string;
        operation: string;
        callback: ((event: Models.IFlexEvent) => void);
    }

    export interface IStompSubscription {
        id: string;
        unsubscribe: () => void;
    }


}

